import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import useFetchObjects from "../../../../../api/useFetchObjects";
import { useAuth } from "../../../../../context";
import { useTranslation } from "react-i18next";

// Styled Components
const StyledBox = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(5),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.1)",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  marginTop: theme.spacing(3),
}));

const StyledTableHeadRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "12px",
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
  },
}));

const ListLaboratoryReferral = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["laboratory-test-orders"],
    `laboratory-test-orders/?doctor=${auth?.user?.user?.id}${
      search ? `&search=${search}` : ""
    }`,
    token
  );

  useEffect(() => {
    refetch();
  }, [search, refetch]);

  const handleOrderView = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDialog = () => {
    setSelectedOrder(null);
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      1: t("laboratory.pending") || "Pending",
      2: "In Progress",
      3: t("laboratory.completed") || "Completed",
      4: t("laboratory.cancelled") || "Cancelled",
    };
    return statusMap[status] || "Unknown";
  };

  const getStatusColor = (status) => {
    if (status === 1) return "warning";
    if (status === 2) return "info";
    if (status === 3) return "success";
    if (status === 4) return "error";
    return "default";
  };

  return (
    <StyledBox>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: "bold",
          textAlign: "center",
          color: theme.palette.primary.main,
        }}
      >
        {t("doctor.labTestReferrals") || "Laboratory Test Referrals"}
      </Typography>

      {/* Search Bar */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <TextField
          variant="outlined"
          placeholder={
            t("doctor.searchByPatientTestType") ||
            "Search by Patient, Test Type"
          }
          fullWidth
          sx={{
            maxWidth: "600px",
            "& .MuiOutlinedInput-root": {
              borderRadius: theme.shape.borderRadius,
            },
            "& .MuiOutlinedInput-input": {
              padding: "10px 14px",
            },
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      {/* Loading Spinner */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error Message */}
      {isError && (
        <Typography
          variant="body1"
          color="error"
          sx={{ textAlign: "center", mt: 4 }}
        >
          {t("doctor.anError")}
        </Typography>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <StyledTableContainer component={Paper}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <StyledTableHeadRow>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("doctor.patient")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.tests") || "Tests"}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.status")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.orderDate")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.totalAmount")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.urgent")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("laboratory.paid")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("doctor.result")}
                </TableCell>
              </StyledTableHeadRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {(data || []).map((referral, index) => (
                <TableRow
                  key={referral.id}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? theme.palette.grey[50] : "white",
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <TableCell align="center">
                    {referral.patient_display?.name || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {referral.tests_display?.length > 0
                      ? `${referral.tests_display.length} test(s)`
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <StyledChip
                      label={getStatusDisplay(referral.status)}
                      color={getStatusColor(referral.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {referral.order_date
                      ? new Date(referral.order_date).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell align="center">{referral.total_amount}؋</TableCell>
                  <TableCell align="center">
                    <StyledChip
                      label={
                        referral.urgency ? t("doctor.yes") : t("doctor.no")
                      }
                      color={referral.urgency ? "error" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <StyledChip
                      label={
                        referral.is_paid ? t("doctor.yes") : t("doctor.no")
                      }
                      color={referral.is_paid ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {referral.tests_display?.some(
                      (test) => test.test_type_display?.result_value
                    ) ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOrderView(referral)}
                      >
                        {t("doctor.viewResult")}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("doctor.noResult")}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      {/* Order Details Dialog */}
      {selectedOrder && (
        <StyledDialog open onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {t("laboratory.testResults") || "Test Results"}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t("doctor.patient")}:</strong>{" "}
              {selectedOrder.patient_display?.name || "-"}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t("laboratory.orderDate")}:</strong>{" "}
              {selectedOrder.order_date
                ? new Date(selectedOrder.order_date).toLocaleString()
                : "-"}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
            >
              {t("laboratory.tests") || "Tests"}
            </Typography>
            {selectedOrder.tests_display?.map((test, index) => (
              <Box
                key={index}
                sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
              >
                <Typography variant="body1" fontWeight={600}>
                  {test.test_type_display?.name || "-"}
                </Typography>
                {test.test_type_display?.result_value && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>{t("laboratory.result")}:</strong>{" "}
                    {test.test_type_display.result_value}{" "}
                    {test.test_type_display.units || ""}
                  </Typography>
                )}
                {test.test_type_display?.reference_range && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t("laboratory.normalRange")}:</strong>{" "}
                    {test.test_type_display.reference_range}
                  </Typography>
                )}
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              color="primary"
              variant="contained"
            >
              {t("doctor.close") || "Close"}
            </Button>
          </DialogActions>
        </StyledDialog>
      )}
    </StyledBox>
  );
};

export default ListLaboratoryReferral;
